import { useState, useCallback } from "react";
import { apiError } from '../lib/errors'
import { Scuttle } from "../components/Scuttle";
import { CropChipPicker } from "../components/shared/CropChipPicker";
import { cropsForLocation } from "../lib/cropRegions";
import type { CropChip } from "../lib/api";

/* ────────────────────────────────────────────────────────────────────────────
   SignupPage — public-facing application form for Grafty
   URL: app.grafty.io/signup  (or grafty.io/join?ref=CODE)

   Flow:
     1. Applicant fills out the form (farm info, goals, what they grow)
     2. POST /api/v1/apply  →  status = "pending"
     3. They see a confirmation screen: "We'll review your application…"
     4. Superadmin sees the application in the admin panel
     5. Admin approves → welcome email sent → they can log in

   Three steps (wizard):
     Step 1 — About you (name, email, phone, location)
     Step 2 — Your farm (type, size, what you grow, goals)
     Step 3 — What you need (plan interest, use cases, referral)
──────────────────────────────────────────────────────────────────────────── */

// ── Types ─────────────────────────────────────────────────────────────────────

interface FormData {
  // Step 1
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  farmName: string;
  farmLocation: string;

  // Step 2
  farmType: string;
  acreage: string;
  crops: CropChip[];
  goalCropYield: number;
  goalMaxProfit: number;
  goalPersonal: number;

  // Step 3
  requestedPlan: "seedling" | "grower" | "estate";
  useCases: string[];
  hearAboutUs: string;
  referralCode: string;
  notes: string;
  agreeTerms: boolean;
  agreePrivacy: boolean;
}

// Operation STYLE — distinct from "which crops" (that's the chip picker below), so
// the two no longer duplicate each other (mango orchard + Mango was redundant).
const FARM_TYPES = [
  { value: "orchard",      label: "Orchard / grove",       icon: "🌳" },
  { value: "food_forest",  label: "Mixed food forest",     icon: "🌿" },
  { value: "vineyard",     label: "Vineyard",              icon: "🍇" },
  { value: "nursery",      label: "Nursery / propagation", icon: "🪴" },
  { value: "hobby",        label: "Hobby / homestead",     icon: "🏡" },
  { value: "mixed",        label: "Mixed / other",         icon: "🌱" },
];

// Bundled crop suggestions for the chip picker at signup (public — no catalog API).
// The backend matches these (and any custom additions) against the growable catalog
// on approval and materializes them into the tenant's varieties.
const CROP_SUGGESTIONS = [
  "Mango", "Peach", "Apple", "Orange", "Lemon", "Lime", "Grapefruit",
  "Avocado", "Lychee", "Longan", "Carambola / star fruit",
  "Papaya", "Banana", "Guava", "Jackfruit", "Sapodilla", "Mamey Sapote",
  "Coconut Palm", "Royal Palm", "Bismarck Palm", "Date Palm",
  "Herbs", "Vegetables", "Berries", "Grapes", "Dragon fruit", "Passionfruit",
].map((c) => ({ common_name: c }));

const USE_CASES = [
  { id: "care_calendar",    label: "Care scheduling",      icon: "📅" },
  { id: "harvest_tracking", label: "Harvest tracking",     icon: "🧺" },
  { id: "qr_codes",         label: "QR tree profiles",     icon: "📱" },
  { id: "irrigation",       label: "Irrigation control",   icon: "💧" },
  { id: "weather",          label: "Weather monitoring",   icon: "⛅" },
  { id: "team",             label: "Team management",      icon: "👥" },
  { id: "reports",          label: "Reports & analytics",  icon: "📊" },
  { id: "commerce",         label: "Online store / sales", icon: "🛒" },
  { id: "ai",               label: "Automatic predictions", icon: "✨" },
  { id: "maps",             label: "Farm mapping",         icon: "🗺️" },
];

const HEAR_ABOUT = [
  "Google search", "Instagram", "Facebook", "A friend or colleague",
  "Farm bureau / extension office", "A referral partner",
  "Mango Mania", "Farmers market / festival", "Other",
];

// Short labels for the compact chooser cards. These must stay TRUE and in step
// with the API plan catalog (grafty-api billing.AllPlans, §238.2) — a bullet may
// only claim what that plan actually gets in production. In particular, analytics
// / irrigation / commerce are NOT plan-gated (§238.3), so they can't be listed as
// paid-tier differentiators. BillingSettings renders the full catalog from the
// API; these are deliberately terser because they render as inline chips.
const PLANS = [
  {
    id: "seedling",
    name: "Seedling",
    price: "Free",
    highlight: false,
    features: ["Up to 25 plants", "Farm map & QR codes", "Care calendar & harvest log", "Yield reports"],
  },
  {
    id: "grower",
    name: "Grower",
    price: "$99 / year",
    highlight: true,
    badge: "Most popular",
    features: ["Everything in Seedling", "250 plants · 10k harvests", "100 pre-orders / month", "Project planning"],
  },
  {
    id: "estate",
    name: "Estate",
    price: "$199 / year",
    highlight: false,
    features: ["Everything in Grower", "Unlimited plants & harvests", "Unlimited pre-orders", "Drone imagery overlays"],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function GoalSlider({ label, sublabel, value, onChange }: {
  label: string; sublabel: string; value: number; onChange: (v: number) => void;
}) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>{label}</div>
          <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{sublabel}</div>
        </div>
        <span style={{
          fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 600,
          color: "var(--brand-mango)", padding: "2px 10px",
          background: "var(--color-bg-mango)", borderRadius: 6,
        }}>{value}%</span>
      </div>
      <div style={{ position: "relative" }}>
        <input
          type="range" min={0} max={100} step={5} value={value}
          onChange={e => onChange(+e.target.value)}
          style={{ width: "100%", accentColor: "var(--brand-mango)" }}
        />
        <div style={{
          display: "flex", justifyContent: "space-between",
          fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 3,
        }}>
          <span>Not a priority</span><span>Very important</span>
        </div>
      </div>
    </div>
  );
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32 }}>
      {Array.from({ length: total }, (_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: i < current ? 24 : 28,
            height: i < current ? 24 : 28,
            borderRadius: "50%",
            background: i + 1 === current
              ? "var(--brand-mango)"
              : i + 1 < current
              ? "var(--brand-green-light)"
              : "var(--color-bg-raised)",
            border: i + 1 === current
              ? "none"
              : i + 1 < current
              ? "none"
              : "1px solid var(--color-border-medium)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700,
            color: i + 1 === current
              ? "#0E1209"
              : i + 1 < current
              ? "#0E1209"
              : "var(--color-text-tertiary)",
            transition: "all 0.2s",
            flexShrink: 0,
          }}>
            {i + 1 < current ? "✓" : i + 1}
          </div>
          {i < total - 1 && (
            <div style={{
              flex: 1, height: 1, minWidth: 32,
              background: i + 1 < current
                ? "var(--brand-green-light)"
                : "var(--color-border)",
            }} />
          )}
        </div>
      ))}
      <span style={{ fontSize: 13, color: "var(--color-text-tertiary)", marginLeft: 4 }}>
        Step {current} of {total}
      </span>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function SignupPage() {
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  // Extract referral code from URL on load
  const urlRef = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("ref") ?? ""
    : "";

  const [form, setForm] = useState<FormData>({
    ownerName: "", ownerEmail: "", ownerPhone: "", farmName: "", farmLocation: "",
    farmType: "orchard", acreage: "", crops: [],
    goalCropYield: 50, goalMaxProfit: 50, goalPersonal: 50,
    requestedPlan: "grower", useCases: [], hearAboutUs: "", referralCode: urlRef,
    notes: "", agreeTerms: false, agreePrivacy: false,
  });

  const set = useCallback(<K extends keyof FormData>(key: K, value: FormData[K]) => {
    setForm(f => ({ ...f, [key]: value }));
  }, []);

  const toggleUseCase = (id: string) => {
    set("useCases", form.useCases.includes(id)
      ? form.useCases.filter(u => u !== id)
      : [...form.useCases, id]);
  };

  // ── Validation per step ────────────────────────────────────────────────────

  const validateStep = (s: number): string | null => {
    if (s === 1) {
      if (!form.ownerName.trim())  return "Please enter your name.";
      if (!form.ownerEmail.trim()) return "Please enter your email address.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.ownerEmail)) return "Please enter a valid email address.";
      if (!form.farmName.trim())   return "Please enter your farm name.";
      if (!form.farmLocation.trim()) return "Please enter your farm location.";
    }
    // Step 2 (crops) is optional — growers can add more anytime after setup.
    if (s === 3) {
      if (!form.hearAboutUs)   return "Please tell us how you heard about Grafty.";
      if (!form.agreeTerms)    return "Please agree to the Terms of Service.";
      if (!form.agreePrivacy)  return "Please agree to the Privacy Policy.";
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep(step);
    if (err) { setError(err); return; }
    setError(null);
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const err = validateStep(3);
    if (err) { setError(err); return; }
    setError(null);
    setLoading(true);

    try {
      const resp = await fetch(`${import.meta.env.VITE_API_URL ?? 'http://localhost:8080'}/api/v1/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          owner_name:       form.ownerName,
          owner_email:      form.ownerEmail,
          owner_phone:      form.ownerPhone,
          farm_name:        form.farmName,
          farm_location:    form.farmLocation,
          farm_type:        form.farmType,
          acreage:          form.acreage ? parseFloat(form.acreage) : null,
          primary_growable: form.crops[0]?.name ?? "", // back-compat: first crop as the "primary"
          crops:            form.crops,                 // full chip list → materialized into varieties on approval
          notes:            form.notes,
          requested_plan:   form.requestedPlan,
          use_cases:        form.useCases,
          hear_about_us:    form.hearAboutUs,
          referral_code:    form.referralCode,
          goal_crop_yield:  form.goalCropYield,
          goal_max_profit:  form.goalMaxProfit,
          goal_personal:    form.goalPersonal,
        }),
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error ?? "Something went wrong. Please try again.");
      }

      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // ── Shared input style ──────────────────────────────────────────────────────
  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "10px 14px", fontSize: 15,
    background: "var(--color-bg-raised)", border: "1px solid var(--color-border-medium)",
    borderRadius: 10, color: "var(--color-text-primary)",
    fontFamily: "var(--font-body)", outline: "none", boxSizing: "border-box",
    transition: "border-color 0.15s",
  };

  const labelStyle: React.CSSProperties = {
    display: "block", fontSize: 13, fontWeight: 500,
    color: "var(--color-text-secondary)", marginBottom: 6,
  };

  const fieldStyle: React.CSSProperties = { marginBottom: 20 };

  // ── Submitted confirmation ─────────────────────────────────────────────────
  if (submitted) {
    const firstName = form.ownerName.split(" ")[0];
    return (
      <div style={{
        minHeight: "100dvh", background: "var(--color-bg-base)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, fontFamily: "var(--font-body)",
      }}>
        <div style={{
          maxWidth: 520, width: "100%", textAlign: "center",
          background: "var(--color-bg-card)", border: "1px solid var(--color-border)",
          borderRadius: 24, padding: "48px 40px",
        }}>

          {/* Scuttle celebrates the submission */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <Scuttle size={120} mood="success" subtitle="Scuttle approves" />
          </div>

          {/* Animated mango sparkle dots */}
          <div style={{
            display: "flex", justifyContent: "center", gap: 6, marginBottom: 28,
          }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 6, height: 6, borderRadius: "50%",
                background: "var(--brand-mango, #F5A623)",
                animation: `bounce 0.8s ease-in-out ${i * 0.15}s infinite alternate`,
              }}/>
            ))}
          </div>
          <style>{`
            @keyframes bounce {
              from { transform: translateY(0); opacity: 0.5; }
              to   { transform: translateY(-6px); opacity: 1; }
            }
          `}</style>

          <h1 style={{
            fontFamily: "var(--font-display, 'Syne', sans-serif)",
            fontSize: "1.75rem", fontWeight: 700,
            color: "var(--color-text-primary)", marginBottom: 10,
            letterSpacing: "-0.02em", lineHeight: 1.2,
          }}>
            You're in the queue, {firstName}!
          </h1>

          <p style={{
            fontSize: 15, color: "var(--color-text-secondary)",
            lineHeight: 1.7, marginBottom: 8,
          }}>
            Your application for{" "}
            <strong style={{ color: "var(--color-text-primary)" }}>{form.farmName}</strong>
            {" "}is being reviewed. We'll be in touch at{" "}
            <strong style={{ color: "var(--brand-mango, #F5A623)" }}>{form.ownerEmail}</strong>
            {" "}within 1–2 business days.
          </p>

          <p style={{
            fontSize: 13, color: "var(--color-text-tertiary)",
            marginBottom: 32,
          }}>
            (We personally review every application — thanks for your patience.)
          </p>

          {/* What happens next */}
          <div style={{
            background: "rgba(245,166,35,0.06)",
            border: "1px solid rgba(245,166,35,0.18)",
            borderRadius: 14, padding: "18px 20px",
            textAlign: "left", marginBottom: 28,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--brand-mango, #F5A623)", marginBottom: 14,
            }}>
              What happens next
            </div>
            {[
              { icon: "📬", text: "We review your application (usually same day)" },
              { icon: "🔑", text: "You'll get an email with your personalised login link" },
              { icon: "🗺️", text: "Map your farm and add your first trees" },
              { icon: "🌳", text: "Start tracking every harvest, every season" },
            ].map(({ icon, text }, i) => (
              <div key={i} style={{
                display: "flex", gap: 12, alignItems: "flex-start",
                marginBottom: i < 3 ? 10 : 0,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                <span style={{ fontSize: 14, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
                  {text}
                </span>
              </div>
            ))}
          </div>

          <a href="https://grafty.io" style={{
            fontSize: 13, color: "var(--color-text-tertiary)",
            textDecoration: "none", display: "inline-block",
          }}>
            ← Back to grafty.io
          </a>
        </div>
      </div>
    );
  }

  // ── Page shell ─────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: "100dvh", background: "var(--color-bg-base)",
      fontFamily: "var(--font-body)", padding: "40px 16px",
    }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>

        {/* Logo */}
        <div style={{
          textAlign: "center", marginBottom: 40,
          fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 700,
          color: "var(--brand-mango)", letterSpacing: "-0.03em",
        }}>
          Grafty 🌿
        </div>

        {/* Card */}
        <div style={{
          background: "var(--color-bg-card)", border: "1px solid var(--color-border)",
          borderRadius: 20, padding: "40px 40px",
        }}>
          <StepIndicator current={step} total={3} />

          {/* Error banner */}
          {error && (
            <div style={{
              background: "var(--color-bg-danger)", border: "1px solid var(--color-border-danger)",
              borderRadius: 10, padding: "12px 16px", marginBottom: 24,
              fontSize: 14, color: "var(--color-text-danger)", display: "flex", gap: 8,
            }}>
              <span>⚠</span> {error}
            </div>
          )}

          {/* ── Step 1: About you ─────────────────────────────────────────── */}
          {step === 1 && (
            <div>
              <h2 style={{
                fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 700,
                letterSpacing: "-0.02em", marginBottom: 6, color: "var(--color-text-primary)",
              }}>About you</h2>
              <p style={{ color: "var(--color-text-tertiary)", fontSize: 14, marginBottom: 28 }}>
                We review every application personally — this helps us get Grafty right for your farm.
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Your name *</label>
                  <input style={inputStyle} placeholder="Jane Smith" value={form.ownerName}
                    onChange={e => set("ownerName", e.target.value)} />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Farm name *</label>
                  <input style={inputStyle} placeholder="Sunshine Mango Grove" value={form.farmName}
                    onChange={e => set("farmName", e.target.value)} />
                </div>
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Email address *</label>
                <input style={inputStyle} type="email" placeholder="jane@yourfarm.com"
                  value={form.ownerEmail} onChange={e => set("ownerEmail", e.target.value)} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Phone number</label>
                  <input style={inputStyle} type="tel" placeholder="(239) 555-0100"
                    value={form.ownerPhone} onChange={e => set("ownerPhone", e.target.value)} />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Farm location *</label>
                  <input style={inputStyle} placeholder="Cape Coral, FL"
                    value={form.farmLocation} onChange={e => set("farmLocation", e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Your farm ─────────────────────────────────────────── */}
          {step === 2 && (
            <div>
              <h2 style={{
                fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 700,
                letterSpacing: "-0.02em", marginBottom: 6, color: "var(--color-text-primary)",
              }}>Your farm</h2>
              <p style={{ color: "var(--color-text-tertiary)", fontSize: 14, marginBottom: 28 }}>
                This helps us tailor Grafty and give you the right recommendations from day one.
              </p>

              {/* Farm type */}
              <div style={fieldStyle}>
                <label style={labelStyle}>What best describes your farm? *</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  {FARM_TYPES.map(ft => (
                    <button key={ft.value}
                      onClick={() => set("farmType", ft.value as FormData["farmType"])}
                      style={{
                        padding: "12px 8px", borderRadius: 10, cursor: "pointer",
                        border: `1px solid ${form.farmType === ft.value ? "var(--brand-mango)" : "var(--color-border-medium)"}`,
                        background: form.farmType === ft.value ? "var(--color-bg-mango)" : "var(--color-bg-raised)",
                        color: form.farmType === ft.value ? "var(--brand-mango)" : "var(--color-text-secondary)",
                        fontSize: 13, fontWeight: form.farmType === ft.value ? 600 : 400,
                        textAlign: "center", transition: "all 0.15s",
                      }}>
                      <div style={{ fontSize: 24, marginBottom: 4 }}>{ft.icon}</div>
                      {ft.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Approximate acreage</label>
                <input style={inputStyle} type="number" min={0} step={0.5} placeholder="e.g. 2.5"
                  value={form.acreage} onChange={e => set("acreage", e.target.value)} />
              </div>

              {/* Crops — a chip picker (replaces the redundant primary-crop select + free-text). */}
              <div style={fieldStyle}>
                <label style={labelStyle}>What do you grow?</label>
                <CropChipPicker
                  value={form.crops}
                  onChange={c => set("crops", c)}
                  suggestions={CROP_SUGGESTIONS}
                  topCrops={cropsForLocation(form.farmLocation)}
                  placeholder="Search crops, or type your own…"
                />
                <p style={{ fontSize: 12, color: "var(--color-text-tertiary)", margin: "8px 0 0" }}>
                  Just your main crops for now — you can add more (and manage varieties) anytime after you're set up.
                </p>
              </div>

              {/* Goal sliders */}
              <div style={{
                background: "var(--color-bg-raised)", borderRadius: 12,
                padding: "20px 20px 8px", marginBottom: 20,
                border: "1px solid var(--color-border)",
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 16 }}>
                  What are your priorities? (helps us personalize your recommendations)
                </div>
                <GoalSlider label="Maximum crop yield" sublabel="Volume of produce per season"
                  value={form.goalCropYield} onChange={v => set("goalCropYield", v)} />
                <GoalSlider label="Maximum profit" sublabel="Revenue and cost optimization"
                  value={form.goalMaxProfit} onChange={v => set("goalMaxProfit", v)} />
                <GoalSlider label="Personal / family use" sublabel="Growing for yourself and loved ones"
                  value={form.goalPersonal} onChange={v => set("goalPersonal", v)} />
              </div>
            </div>
          )}

          {/* ── Step 3: What you need ──────────────────────────────────────── */}
          {step === 3 && (
            <div>
              <h2 style={{
                fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 700,
                letterSpacing: "-0.02em", marginBottom: 6, color: "var(--color-text-primary)",
              }}>What you need</h2>
              <p style={{ color: "var(--color-text-tertiary)", fontSize: 14, marginBottom: 28 }}>
                Tell us what features matter most to you and which plan you're interested in.
              </p>

              {/* Plan selection */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Which plan are you interested in?</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {PLANS.map(plan => (
                    <div key={plan.id}
                      onClick={() => set("requestedPlan", plan.id as FormData["requestedPlan"])}
                      style={{
                        border: `1px solid ${form.requestedPlan === plan.id ? "var(--brand-mango)" : "var(--color-border-medium)"}`,
                        background: form.requestedPlan === plan.id ? "var(--color-bg-mango)" : "var(--color-bg-raised)",
                        borderRadius: 12, padding: "14px 16px", cursor: "pointer",
                        display: "flex", alignItems: "flex-start", gap: 14,
                        transition: "all 0.15s",
                      }}>
                      {/* Radio */}
                      <div style={{
                        width: 18, height: 18, borderRadius: "50%", flexShrink: 0, marginTop: 2,
                        border: `2px solid ${form.requestedPlan === plan.id ? "var(--brand-mango)" : "var(--color-border-medium)"}`,
                        background: form.requestedPlan === plan.id ? "var(--brand-mango)" : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {form.requestedPlan === plan.id && (
                          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#0E1209" }} />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, color: "var(--color-text-primary)", fontSize: 15 }}>
                            {plan.name}
                          </span>
                          <span style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>
                            {plan.price}
                          </span>
                          {plan.badge && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: "2px 8px",
                              borderRadius: 20, background: "var(--brand-mango)", color: "#0E1209",
                              letterSpacing: "0.04em", textTransform: "uppercase",
                            }}>{plan.badge}</span>
                          )}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
                          {plan.features.map(f => (
                            <span key={f} style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                              ✓ {f}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 8, margin: 0 }}>
                  Not sure? Choose Grower — we can adjust when we review your application.
                </p>
              </div>

              {/* Use cases */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Which features are most important to you? (select all that apply)</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
                  {USE_CASES.map(uc => {
                    const checked = form.useCases.includes(uc.id);
                    return (
                      <div key={uc.id} onClick={() => toggleUseCase(uc.id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "10px 14px", borderRadius: 10, cursor: "pointer",
                          border: `1px solid ${checked ? "var(--brand-mango)" : "var(--color-border)"}`,
                          background: checked ? "var(--color-bg-mango)" : "var(--color-bg-raised)",
                          transition: "all 0.15s",
                        }}>
                        <span style={{ fontSize: 18 }}>{uc.icon}</span>
                        <span style={{
                          fontSize: 13, fontWeight: checked ? 500 : 400,
                          color: checked ? "var(--brand-mango)" : "var(--color-text-secondary)",
                        }}>{uc.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Hear about us */}
              <div style={fieldStyle}>
                <label style={labelStyle}>How did you hear about Grafty? *</label>
                <select style={inputStyle} value={form.hearAboutUs}
                  onChange={e => set("hearAboutUs", e.target.value)}>
                  <option value="">Select…</option>
                  {HEAR_ABOUT.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>

              {/* Referral code (pre-filled from URL, editable) */}
              {(form.referralCode || urlRef) && (
                <div style={fieldStyle}>
                  <label style={labelStyle}>Referral code</label>
                  <input style={{ ...inputStyle, fontFamily: "var(--font-mono)" }}
                    value={form.referralCode} onChange={e => set("referralCode", e.target.value)}
                    placeholder="e.g. JSMITH-GROVE" />
                </div>
              )}

              {/* Additional notes */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Anything else you'd like us to know? (optional)</label>
                <textarea style={{ ...inputStyle, height: "auto", minHeight: 80, padding: "10px 14px", resize: "vertical" }}
                  placeholder="Tell us about your farm, goals, or any questions you have…"
                  value={form.notes} onChange={e => set("notes", e.target.value)} />
              </div>

              {/* Legal checkboxes */}
              <div style={{ marginBottom: 24 }}>
                {[
                  { key: "agreeTerms" as const, text: <>I agree to Grafty's <a href="/terms" target="_blank" rel="noopener" onClick={e => e.stopPropagation()} style={{ color: "var(--brand-mango)" }}>Terms of Service</a> *</> },
                  { key: "agreePrivacy" as const, text: <>I agree to Grafty's <a href="/privacy" target="_blank" rel="noopener" onClick={e => e.stopPropagation()} style={{ color: "var(--brand-mango)" }}>Privacy Policy</a> *</> },
                ].map(({ key, text }) => (
                  <div key={key} onClick={() => set(key, !form[key])}
                    style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12, cursor: "pointer" }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
                      border: `2px solid ${form[key] ? "var(--brand-mango)" : "var(--color-border-medium)"}`,
                      background: form[key] ? "var(--brand-mango)" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.15s",
                    }}>
                      {form[key] && <span style={{ color: "#0E1209", fontSize: 12, fontWeight: 700 }}>✓</span>}
                    </div>
                    <span style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Navigation ──────────────────────────────────────────────────── */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            {step > 1 ? (
              <button onClick={() => { setError(null); setStep(s => s - 1); }}
                style={{
                  padding: "10px 24px", borderRadius: 10, cursor: "pointer",
                  border: "1px solid var(--color-border-medium)",
                  background: "var(--color-bg-raised)", color: "var(--color-text-secondary)",
                  fontSize: 14, fontWeight: 500, fontFamily: "var(--font-body)",
                }}>
                ← Back
              </button>
            ) : (
              <a href="https://grafty.io" style={{ fontSize: 14, color: "var(--color-text-tertiary)", textDecoration: "none" }}>
                ← Back to site
              </a>
            )}

            {step < 3 ? (
              <button onClick={handleNext}
                style={{
                  padding: "10px 28px", borderRadius: 10, cursor: "pointer",
                  background: "var(--brand-mango)", border: "none",
                  color: "#0E1209", fontSize: 14, fontWeight: 600,
                  fontFamily: "var(--font-body)", transition: "all 0.15s",
                }}>
                Continue →
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading}
                style={{
                  padding: "12px 32px", borderRadius: 10, cursor: loading ? "not-allowed" : "pointer",
                  background: loading ? "var(--color-bg-raised)" : "var(--brand-mango)",
                  border: "none", color: loading ? "var(--color-text-tertiary)" : "#0E1209",
                  fontSize: 15, fontWeight: 600, fontFamily: "var(--font-body)",
                  display: "flex", alignItems: "center", gap: 8,
                  opacity: loading ? 0.7 : 1, transition: "all 0.15s",
                }}>
                {loading ? (
                  <><span style={{ display: "inline-block", animation: "spin 0.8s linear infinite" }}>⟳</span> Submitting…</>
                ) : (
                  "Submit application →"
                )}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <p style={{
          textAlign: "center", fontSize: 12, color: "var(--color-text-tertiary)",
          marginTop: 24, lineHeight: 1.7,
        }}>
          Already have an account?{" "}
          <a href="/login" style={{ color: "var(--brand-mango)" }}>Sign in</a>
          {" "}·{" "}
          <a href="/privacy" target="_blank" rel="noopener" style={{ color: "var(--color-text-tertiary)" }}>Privacy</a>
          {" "}·{" "}
          <a href="/terms" target="_blank" rel="noopener" style={{ color: "var(--color-text-tertiary)" }}>Terms</a>
        </p>
      </div>
    </div>
  );
}
